import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin,
  CalendarClock,
  HeartHandshake,
  Users,
  Ticket,
  Trophy,
  FileSignature,
  FileDown,
  Send,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { FadeUp, MaskedLines, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FACTS = [
  {
    id: "fact-format",
    icon: Users,
    k: "8+1 pe teren",
    v: "Echipe de 9 jucători, pe teren de fotbal full-size — nu minifotbal.",
  },
  {
    id: "fact-taxa",
    icon: Ticket,
    k: "Taxă simbolică",
    v: "O taxă mică de înscriere per echipă, integral pentru cauză.",
  },
  {
    id: "fact-cauza",
    icon: HeartHandshake,
    k: "Scop caritabil",
    v: "Fondurile susțin proiectele Asociației Art & Outdoor pentru copii și tineri.",
  },
  {
    id: "fact-loc",
    icon: MapPin,
    k: "Alba Iulia",
    v: "Terenul exact și data sunt în curs de stabilire — le comunicăm echipelor înscrise.",
  },
];

const TYPES = [
  { id: "echipa", label: "Înscriu o echipă", testid: "cupa-type-echipa" },
  { id: "sponsor", label: "Vreau să sponsorizez", testid: "cupa-type-sponsor" },
  { id: "voluntar", label: "Vreau să fiu voluntar", testid: "cupa-type-voluntar" },
  { id: "notificare", label: "Doar anunță-mă", testid: "cupa-type-notificare" },
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-carddark px-4 py-3.5 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors duration-300 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon";

const labelCls =
  "mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400";

const Hero = () => (
  <section
    data-testid="cupa-hero"
    className="relative overflow-hidden border-b border-white/10 bg-canvas"
  >
    <div className="mx-auto max-w-7xl px-4 pb-20 pt-36 sm:px-8 lg:px-12 lg:pb-28 lg:pt-44">
      <Overline>Eveniment caritabil · Asociația Art &amp; Outdoor România</Overline>
      <h1
        data-testid="cupa-page-title"
        className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl"
      >
        <MaskedLines
          lines={[
            <>Mini-campionat</>,
            <span key="l2" className="text-neon">de fotbal caritabil</span>,
          ]}
        />
      </h1>
      <FadeUp delay={0.5}>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg">
          Un turneu de fotbal pentru comunitate, organizat în Alba Iulia.
          Echipe de 8+1 pe teren full-size, taxă simbolică de înscriere, iar toți
          banii merg în proiectele noastre pentru copii și tineri.
        </p>
      </FadeUp>
      <FadeUp delay={0.6}>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-neutral-400">
          Eveniment în cadrul proiectului{" "}
          <Link
            to="/bushido-vs-bullying"
            data-testid="cupa-bushido-link"
            className="text-neon underline-offset-4 hover:underline"
          >
            Bushido vs. Bullying
          </Link>{" "}
          — fair-play, respect și zero discriminare, pe teren și în tribună.
        </p>
      </FadeUp>
      <FadeUp delay={0.65} className="mt-10 flex flex-wrap gap-3">
        <span
          data-testid="cupa-date-badge"
          className="flex items-center gap-2 rounded-full border border-neon/40 bg-surface px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon"
        >
          <CalendarClock className="h-3.5 w-3.5" /> Toamna / iarna 2026 · data se anunță
        </span>
        <span className="flex items-center gap-2 rounded-full border border-white/15 bg-surface px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">
          <MapPin className="h-3.5 w-3.5" /> Alba Iulia · locul și data se stabilesc
        </span>
      </FadeUp>
      <FadeUp delay={0.8} className="mt-10 flex flex-wrap gap-3">
        <a
          href="#inscriere"
          data-testid="cupa-hero-cta"
          className="inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
        >
          <Trophy className="h-4 w-4" /> Înscrie o echipă
        </a>
        <Link
          to="/cupa-caritabila/inscriere"
          data-testid="cupa-official-form-link"
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-neon hover:text-neon"
        >
          <FileSignature className="h-4 w-4" /> Formular oficial cu semnătură
        </Link>
        <a
          href={`${API}/cupa/formular-tipar?copies=12`}
          data-testid="cupa-print-form-link"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-300 transition-colors duration-300 hover:border-neon hover:text-neon"
        >
          <FileDown className="h-4 w-4" /> 12 formulare PDF pentru printare
        </a>
      </FadeUp>
    </div>
  </section>
);

const Facts = () => (
  <section data-testid="cupa-facts-section" className="bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-24">
      <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2">
        {FACTS.map(({ icon: Icon, ...f }, i) => (
          <FadeUp key={f.id} delay={i * 0.08} className="bg-surface">
            <div data-testid={f.id} className="group h-full p-8 transition-colors duration-500 hover:bg-carddark">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-canvas text-neon transition-transform duration-500 group-hover:-rotate-6">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-6 font-display text-lg font-black uppercase tracking-tight text-white">
                {f.k}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">{f.v}</p>
            </div>
          </FadeUp>
        ))}
      </div>

      <FadeUp delay={0.2}>
        <div
          data-testid="cupa-details-note"
          className="mt-12 flex max-w-2xl items-start gap-3 rounded-2xl border-l-2 border-flame bg-carddark p-6"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
          <p className="text-sm leading-relaxed text-neutral-300">
            <strong className="text-white">Detaliile se stabilesc.</strong> Data
            exactă, formatul meciurilor, taxa de înscriere și programul se anunță
            în curând. Lasă-ne datele tale și te contactăm imediat ce e totul
            confirmat.
          </p>
        </div>
      </FadeUp>
    </div>
  </section>
);

const SignupForm = () => {
  const [type, setType] = useState("echipa");
  const [form, setForm] = useState({
    name: "",
    team_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/cupa-signup`, {
        type,
        name: form.name.trim() || null,
        team_name: type === "echipa" ? form.team_name.trim() || null : null,
        email: form.email,
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      setDone(true);
      setForm({ name: "", team_name: "", email: "", phone: "", message: "" });
      toast.success("Te-am notat! Revenim cu detaliile imediat ce le avem.");
    } catch (err) {
      toast.error("Ceva nu a mers bine. Te rugăm să încerci din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="inscriere"
      data-testid="cupa-signup-section"
      className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-32"
    >
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <FadeUp className="lg:col-span-5">
          <Overline>Înscriere &amp; sprijin</Overline>
          <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
            Vino cu <span className="text-neon">echipa ta</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-neutral-400">
            Poți înscrie o echipă, poți sprijini turneul ca sponsor, poți veni
            voluntar sau doar să fii anunțat când stabilim data. Orice variantă
            ajută la fel de mult.
          </p>
          <p className="mt-8 text-sm leading-relaxed text-neutral-500">
            Ai întrebări? Scrie-ne direct la{" "}
            <a
              href="mailto:contact@artandoutdoor.com"
              className="text-neon underline-offset-4 hover:underline"
            >
              contact@artandoutdoor.com
            </a>{" "}
            sau prin{" "}
            <Link to="/contact" data-testid="cupa-contact-link" className="text-neon underline-offset-4 hover:underline">
              pagina de contact
            </Link>
            .
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-7">
          <form
            data-testid="cupa-signup-form"
            onSubmit={submit}
            className="rounded-3xl border border-white/10 bg-surface p-8 sm:p-10"
          >
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={t.testid}
                  onClick={() => setType(t.id)}
                  className={`rounded-full px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                    type === t.id
                      ? "bg-neon text-canvas"
                      : "border border-white/15 text-neutral-400 hover:border-neon hover:text-neon"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {type === "echipa" && (
                <div className="sm:col-span-2">
                  <label htmlFor="cupa-team" className={labelCls}>
                    Numele echipei
                  </label>
                  <input
                    id="cupa-team"
                    data-testid="cupa-input-team"
                    type="text"
                    required
                    value={form.team_name}
                    onChange={set("team_name")}
                    placeholder="Ex: FC Alba"
                    className={inputCls}
                  />
                </div>
              )}
              <div>
                <label htmlFor="cupa-name" className={labelCls}>
                  {type === "echipa" ? "Persoană de contact" : "Nume"}
                </label>
                <input
                  id="cupa-name"
                  data-testid="cupa-input-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Numele tău"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cupa-email" className={labelCls}>
                  Email
                </label>
                <input
                  id="cupa-email"
                  data-testid="cupa-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="nume@email.ro"
                  className={inputCls}
                />
              </div>
              {type !== "notificare" && (
                <div className="sm:col-span-2">
                  <label htmlFor="cupa-phone" className={labelCls}>
                    Telefon
                  </label>
                  <input
                    id="cupa-phone"
                    data-testid="cupa-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="07xx xxx xxx"
                    className={inputCls}
                  />
                </div>
              )}
              <div className="sm:col-span-2">
                <label htmlFor="cupa-message" className={labelCls}>
                  Mesaj (opțional)
                </label>
                <textarea
                  id="cupa-message"
                  data-testid="cupa-input-message"
                  rows={4}
                  maxLength={1500}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={
                    type === "sponsor"
                      ? "Cum ai vrea să sprijini turneul?"
                      : "Orice detaliu util pentru noi"
                  }
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="cupa-submit-button"
              disabled={loading}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white disabled:opacity-60 sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Se trimite...
                </>
              ) : (
                <>
                  Trimite <Send className="h-4 w-4" />
                </>
              )}
            </button>

            {done && (
              <p
                data-testid="cupa-success-message"
                className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-neon"
              >
                Am primit datele · te contactăm în curând
              </p>
            )}
          </form>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Cupa() {
  return (
    <>
      <Hero />
      <Facts />
      <SignupForm />
    </>
  );
}
